import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { getAppStopSetting, saveAppStopSetting } from '../../api/appStop';
import { errorMessages } from '../../utils/methods';

// 센터 설정 > 회원앱 중지 — 지점별로 회원앱에서 회원권(수강권·이용권) 중지를 허용할지(2026-11-03).
// 끄면 회원앱 중지만 막힌다. CRM 중지·회원앱 중지 해제는 그대로. 기준은 회원권의 판매지점.
function AppStopSettings({ branchId }) {
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['appStopSetting', branchId],
    queryFn: () => getAppStopSetting(branchId, user.token),
    enabled: !!branchId,
  });

  const saveMutation = useMutation({
    mutationFn: enabled => saveAppStopSetting(branchId, enabled, user.token),
    onSuccess: saved => {
      toast.success(saved?.enabled ? '회원앱 중지를 허용했습니다' : '회원앱 중지를 막았습니다');
      queryClient.invalidateQueries({ queryKey: ['appStopSetting', branchId] });
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const enabled = data?.enabled ?? true;

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          background: '#fff', borderRadius: '12px', border: '1px solid #E8E8E8',
          padding: '24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px',
        }}>
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>회원앱 회원권 중지</Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mt: '4px' }}>
            회원이 앱에서 직접 수강권·이용권을 중지할 수 있게 합니다. 끄면 센터(CRM)에서만 중지할 수 있고, 앱에서
            중지하면 &lsquo;센터에 문의해 주세요&rsquo; 안내가 나옵니다.
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mt: '2px' }}>
            중지 해제는 앱에서도 그대로 됩니다. 이 지점에서 판매한 회원권에 적용됩니다.
          </Typography>
          {isError && (
            <Typography sx={{ fontSize: '13px', color: '#d32f2f', mt: '8px' }}>설정을 불러오지 못했습니다.</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: enabled ? '#0F7F85' : '#8C8C8C' }}>
            {enabled ? '허용' : '막음'}
          </Typography>
          <Switch
            checked={enabled}
            // 지점을 아직 모르거나 설정을 못 읽었으면 누를 수 없게 한다(기본값 '허용'으로 잘못 저장하지 않게)
            disabled={!branchId || !data || isLoading || isError || saveMutation.isPending}
            onChange={e => saveMutation.mutate(e.target.checked)}
            inputProps={{ 'aria-label': '회원앱 회원권 중지 허용' }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AppStopSettings;
